import React from 'react';
import { Alert, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { DailyTradingAdviceResponse, HistoryPlaybackRecord } from '@/api/weatherTradingAdvice';

const { Text } = Typography;

function fmtMw(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return Math.round(value).toLocaleString('zh-CN');
}

function fmtPct(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return `${Math.round(value * 100)}%`;
}

const WeatherHistoryPlaybackTab: React.FC<{ data: DailyTradingAdviceResponse }> = ({ data }) => {
  const playback = data.history_playback;
  const highPv = playback.high_pv_realization_summary;
  const columns: ColumnsType<HistoryPlaybackRecord> = [
    { title: '日期', dataIndex: 'date', width: 110 },
    {
      title: '回放动作',
      dataIndex: 'replayed_action',
      width: 90,
      render: (value: string) => <Tag>{value === 'conservative' ? '偏保守' : value === 'aggressive' ? '偏激进' : '正常'}</Tag>,
    },
    { title: '官方MW', dataIndex: 'official_midday_mw', align: 'right', render: fmtMw },
    { title: '建议MW', dataIndex: 'recommended_realizable_mw', align: 'right', render: fmtMw },
    { title: '实际MW', dataIndex: 'actual_midday_mw', align: 'right', render: fmtMw },
    { title: '误差MW', dataIndex: 'mw_error', align: 'right', render: fmtMw },
    {
      title: '结果',
      dataIndex: 'direction_result',
      render: (value: string) => <Tag color={value === 'hit' ? 'green' : 'default'}>{value === 'hit' ? '命中' : '中性'}</Tag>,
    },
    { title: '归因', dataIndex: 'cause_label' },
  ];

  return (
    <section style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 14 }}>
      <Alert type="info" showIcon message="历史效果为模型回放" description={playback.mode_note} style={{ marginBottom: 12 }} />
      {highPv && (
        <section style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, marginBottom: 12, background: '#fafafa' }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>高光伏兑现回测</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 10 }}>
            <Metric label="回测窗口" value={`${highPv.sample_days || '-'} 天`} />
            <Metric label="高光伏样本" value={`${highPv.high_pv_days || '-'} 天`} />
            <Metric label="平均兑现率" value={fmtPct(highPv.avg_realization_ratio)} danger={(highPv.avg_realization_ratio ?? 1) < 0.75} />
            <Metric label="弱/未兑现占比" value={fmtPct(highPv.weak_or_failed_rate)} danger={(highPv.weak_or_failed_rate ?? 0) >= 0.5} />
            <Metric label="失败占比" value={fmtPct(highPv.failed_rate)} danger={(highPv.failed_rate ?? 0) >= 0.3} />
            <Metric label="平均缺口" value={`${fmtMw(highPv.avg_shortfall_mw)} MW`} danger={(highPv.avg_shortfall_mw ?? 0) >= 5000} />
            <Metric label="疑似限电天数" value={`${highPv.curtailment_suspected_days ?? 0} 天`} />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <Tag>P10 {fmtPct(highPv.p10_realization_ratio)}</Tag>
            <Tag>P50 {fmtPct(highPv.p50_realization_ratio)}</Tag>
            <Tag>P90 {fmtPct(highPv.p90_realization_ratio)}</Tag>
            <Tag>P80缺口 {fmtMw(highPv.p80_shortfall_mw)} MW</Tag>
          </div>
          <Text style={{ display: 'block', marginBottom: 6 }}>{highPv.business_interpretation}</Text>
          <Text type="secondary" style={{ display: 'block' }}>{highPv.snapshot_note}</Text>
        </section>
      )}
      <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
        <Text>样本天数：{playback.summary.days ?? '-'}</Text>
        <Text>方向命中率：{fmtPct(playback.summary.direction_hit_rate)}</Text>
        <Text>平均误差：{fmtMw(playback.summary.avg_mw_error)} MW</Text>
        <Text>高风险天数：{playback.summary.high_risk_days ?? '-'}</Text>
      </div>
      <Table<HistoryPlaybackRecord>
        rowKey={(record) => record.date ?? `${record.official_midday_mw}-${record.actual_midday_mw}`}
        size="small"
        columns={columns}
        dataSource={playback.records}
        pagination={false}
        scroll={{ x: 900 }}
      />
    </section>
  );
};

const Metric: React.FC<{ label: string; value: string; danger?: boolean }> = ({ label, value, danger }) => (
  <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', padding: '8px 10px', minWidth: 0 }}>
    <Text type="secondary" style={{ fontSize: 12 }}>{label}</Text>
    <div style={{ fontSize: 18, fontWeight: 700, color: danger ? '#a8071a' : '#1f2937' }}>{value}</div>
  </div>
);

export default WeatherHistoryPlaybackTab;
